import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { apiClient } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

interface QueueConfig {
  tenant_id: string
  location_id: string
  service_id: string
  location_name?: string
  service_name?: string
  image_url?: string
  announcement?: string
  party_size_enabled?: boolean
  max_party_size?: number
  questions: Array<{
    key: string
    label: string
    type: string
    required: boolean
    options?: string[]
  }>
}

interface BrandingConfig {
  company_name?: string
  logo_url?: string
  primary_color?: string
  secondary_color?: string
  web_join_title?: string
  web_join_subtitle?: string
  web_join_footer?: string
  web_join_background_color?: string
  web_join_text_color?: string
  web_join_button_color?: string
  web_join_success_message?: string
  web_join_error_message?: string
}

export function JoinQueue() {
  const { tenantId, locationId, serviceId } = useParams<{
    tenantId: string
    locationId: string
    serviceId: string
  }>()
  
  const [config, setConfig] = useState<QueueConfig | null>(null)
  const [branding, setBranding] = useState<BrandingConfig>({})
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState<{ position: number; queueName: string; customMessage?: string } | null>(null)
  const [consent, setConsent] = useState(false)
  
  const [formData, setFormData] = useState({
    phone: '',
    name: '',
    party_size: 1, // Start at 1 (default party size)
    answers: {} as Record<string, string>
  })

  useEffect(() => {
    if (!tenantId || !locationId || !serviceId) return
    
    const fetchConfig = async () => {
      try {
        setLoading(true)
        const queueResponse = await apiClient.getQueueConfig(tenantId, locationId, serviceId)
        setConfig(queueResponse)
        
        // Try to get branding settings from the queue config response
        if (queueResponse.branding) {
          setBranding(queueResponse.branding)
        }
      } catch (err) {
        setError('Queue not found or unavailable')
      } finally {
        setLoading(false)
      }
    }
    
    fetchConfig()
  }, [tenantId, locationId, serviceId])

  const normalizePhone = (phone: string): string => {
    const digits = phone.replace(/\D/g, '')
    if (digits.length === 10) return `+1${digits}`
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`
    if (phone.startsWith('+') && /^\+\d{10,15}$/.test(phone)) return phone
    return ''
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!config || !tenantId || !locationId || !serviceId) return
    
    const normalizedPhone = normalizePhone(formData.phone)
    if (!normalizedPhone) {
      setError('Please enter a valid phone number (10 digits)')
      return
    }
    
    if (!consent) {
      setError('Please agree to receive SMS notifications')
      return
    }
    
    // Validate party size if enabled
    if (config.party_size_enabled && formData.party_size < 1) {
      setError('Please select your party size using the +/- buttons')
      return
    }
    
    // Validate required questions
    for (const question of config.questions) {
      if (question.required && !formData.answers[question.key]?.trim()) {
        setError(`Please answer: ${question.label}`)
        return
      }
    }
    
    setSubmitting(true)
    setError('')
    
    try {
      const result = await apiClient.joinQueue({
        tenant_id: tenantId,
        location_id: locationId,
        service_id: serviceId,
        phone: normalizedPhone,
        name: formData.name,
        party_size: config.party_size_enabled ? formData.party_size : undefined,
        agree_version: 'v1',
        meta: { answers: formData.answers }
      })
      
      if (result.error) {
        setError(result.error)
        return
      }
      
      setSuccess({
        position: result.position,
        queueName: result.queue_name || `${locationId} / ${serviceId}`,
        customMessage: result.custom_message
      })
    } catch (err: any) {
      const errorMsg = branding.web_join_error_message || err?.message || 'Network error. Please try again.'
      setError(errorMsg)
    } finally {
      setSubmitting(false)
    }
  }

  // Apply branding styles
  const bgColor = branding.web_join_background_color || '#f9fafb'
  const textColor = branding.web_join_text_color || '#111827'
  const buttonColor = branding.web_join_button_color || '#3C226E'
  // const primaryColor = branding.primary_color || '#3C226E'
  const displayImage = config?.image_url || branding.logo_url
  const pageTitle = branding.web_join_title || 'Join Queue'
  const companyName = branding.company_name || tenantId

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: bgColor, color: textColor }}>
        <Card className="w-full max-w-md">
          <CardContent className="p-6">
            <div className="text-center">Loading queue...</div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!config) {
    const errorMsg = branding.web_join_error_message || 'The requested queue does not exist or is unavailable.'
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: bgColor, color: textColor }}>
        <Card className="w-full max-w-md">
          <CardContent className="p-6">
            <div className="text-center">
              <h1 className="text-xl font-bold text-red-600 mb-2">Queue Not Found</h1>
              <p className="text-gray-600">{errorMsg}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (success) {
    const successMsg = success.customMessage || branding.web_join_success_message || `Successfully joined ${success.queueName}!`
    return (
      <div className="min-h-screen bg-green-50 flex items-center justify-center p-4" style={{ backgroundColor: bgColor, color: textColor }}>
        <Card className="w-full max-w-md">
          <CardContent className="p-6">
            <div className="text-center">
              {displayImage && (
                <img 
                  src={displayImage} 
                  alt={companyName} 
                  className="w-full max-h-48 object-contain rounded-lg mb-4 bg-gray-50"
                />
              )}
              <h1 className="text-xl font-bold text-green-600 mb-2">
                {successMsg}
              </h1>
              <div className="bg-green-600 text-white rounded-full px-4 py-2 inline-block mb-4">
                Position: {success.position}
              </div>
              <p className="text-gray-600">
                You'll receive SMS updates about your position. You can close this page.
              </p>
              {branding.web_join_footer && (
                <div className="mt-4 pt-4 border-t text-sm text-gray-500">
                  {branding.web_join_footer}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: bgColor, color: textColor }}>
      <Card className="w-full max-w-md">
        <CardHeader>
          {displayImage && (
            <img 
              src={displayImage} 
              alt={companyName} 
              className="w-full max-h-48 object-contain rounded-lg mb-4 bg-gray-50"
            />
          )}
          <CardTitle className="text-center" style={{ color: textColor }}>{pageTitle}</CardTitle>
          {branding.web_join_subtitle && (
            <div className="text-center text-sm text-gray-600 mb-2">
              {branding.web_join_subtitle}
            </div>
          )}
          <div className="text-center text-sm text-gray-600 bg-gray-100 p-2 rounded">
            {config.location_name || locationId} • {config.service_name || serviceId}
          </div>
          {config.announcement && (
            <div 
              className="bg-blue-50 border border-blue-200 p-3 rounded text-blue-800 text-sm"
              dangerouslySetInnerHTML={{ __html: config.announcement }}
            />
          )}
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="555-123-4567 or +1 555 123 4567"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="name">Your Name (optional)</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Jane Doe"
              />
            </div>
            
            {config.party_size_enabled && (
              <div className="space-y-3">
                <Label htmlFor="party_size" className="block mb-2">
                  Party Size *
                </Label>

                {/* Plus/Minus Control */}
                <div className="flex items-center gap-0 border border-gray-300 rounded-xl overflow-hidden bg-white">
                  <button
                    type="button"
                    onClick={() => {
                      const newValue = Math.max(1, formData.party_size - 1)
                      setFormData(prev => ({ ...prev, party_size: newValue }))
                    }}
                    disabled={formData.party_size <= 1}
                    className="flex-shrink-0 w-16 h-16 flex items-center justify-center text-4xl font-bold text-white bg-purple-600 hover:bg-purple-700 active:scale-95 disabled:bg-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed transition-all"
                    aria-label="Decrease party size"
                  >
                    −
                  </button>

                  <div className="flex-1 flex flex-col items-center justify-center h-16 border-l border-r border-gray-300">
                    <div className="text-3xl font-bold text-gray-900">
                      {formData.party_size === 0 ? '?' : formData.party_size}
                    </div>
                    <div className="text-xs text-gray-500">
                      {formData.party_size === 0 ? 'Select size' : formData.party_size === 1 ? 'person' : 'people'}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      const maxSize = config.max_party_size || 10
                      const newValue = Math.min(maxSize, formData.party_size + 1)
                      setFormData(prev => ({ ...prev, party_size: newValue }))
                    }}
                    disabled={formData.party_size >= (config.max_party_size || 10)}
                    className="flex-shrink-0 w-16 h-16 flex items-center justify-center text-4xl font-bold text-white bg-purple-600 hover:bg-purple-700 active:scale-95 disabled:bg-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed transition-all"
                    aria-label="Increase party size"
                  >
                    +
                  </button>
                </div>

                <div className="text-center text-sm text-gray-600 mt-2">
                  How many people in your party? (Max: {config.max_party_size || 10})
                </div>
              </div>
            )}
            
            {config.questions.map((question) => (
              <div key={question.key} className="space-y-2">
                <Label htmlFor={question.key}>
                  {question.label}
                  {question.required && <span className="text-red-500 ml-1">*</span>}
                </Label>
                
                {question.type === 'select' && question.options ? (
                  <Select
                    value={formData.answers[question.key] || ''}
                    onValueChange={(value) => setFormData(prev => ({
                      ...prev,
                      answers: { ...prev.answers, [question.key]: value }
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent>
                      {question.options.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id={question.key}
                    type={question.type === 'number' ? 'number' : 'text'}
                    value={formData.answers[question.key] || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      answers: { ...prev.answers, [question.key]: e.target.value }
                    }))}
                    required={question.required}
                  />
                )}
              </div>
            ))}
            
            <div className="flex items-start space-x-2 bg-gray-50 p-3 rounded">
              <Checkbox
                id="consent"
                checked={consent}
                onCheckedChange={(checked) => setConsent(checked === true)}
                required
              />
              <Label htmlFor="consent" className="text-sm leading-relaxed">
                I consent to receive text messages about my place in line and understand that 
                message and data charges from my carrier are my responsibility.
                <span className="text-red-500 ml-1">*</span>
              </Label>
            </div>
            
            {error && (
              <div className="text-red-600 text-sm bg-red-50 p-2 rounded">
                {error}
              </div>
            )}
            
            <Button 
              type="submit" 
              className="w-full" 
              disabled={submitting}
              style={{ backgroundColor: buttonColor, borderColor: buttonColor }}
            >
              {submitting ? 'Joining Queue...' : 'Join Queue'}
            </Button>
          </form>
          
          {branding.web_join_footer && (
            <div className="mt-4 pt-4 border-t text-center text-sm text-gray-500">
              {branding.web_join_footer}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}